import React, { useMemo, useState } from 'react';

import { AlertBanner, AlertBannerStyles } from 'src/app/components/lib/alert-banner';
import { ControlBar, ControlGroup, ControlSearchBox } from 'src/app/components/lib/control-bar';
import { ModalDataGrid } from 'src/app/components/lib/table';
import { FullWidthCardContainer, WizardCardList } from 'src/app/components/lib/wizard/card';
import { FilterButton, FilterButtonList, FilterType } from 'src/app/components/lib/wizard/filters';
import { MarketingDiscountsDiscountWizardProductList } from 'src/app/constants/table-names';
import { useDiscountProductsQuery } from 'src/app/queries/discounts/get-discount-products';

import { FILTER_RESOURCES } from './constants';
import {
  FilterCardWrapper,
  ProductListDescription,
  ProductListGridWrapper,
  ProductListHeader,
  ProductListHeading,
  ProductListWrapper,
} from './styled';
import { useConditionalFilterParameters } from './use-conditional-filter-parameters';
import { useFilterResourceData } from './use-filter-resource-data';
import { useFilteredProducts } from './use-filtered-products';
import { useProductListColumns } from './use-product-list-columns';

import type { Filter as FiltersFilterType } from '../state/requirements';

export type FilterResourceType = (typeof FILTER_RESOURCES)[keyof typeof FILTER_RESOURCES];

export type FilterResource = {
  resourceId: number;
  resourceName: string;
  resourceNumberOfProducts?: number;
};

export function Filter({ requirement }: { requirement: number | 'reward' }) {
  // state
  const [searchString, setSearchString] = useState('');

  // conditionals for requirement products filters versus reward products filters
  const { description, filterButtonClickHandler, filters, heading, noFilterWarningFragment } =
    useConditionalFilterParameters(requirement);

  // data
  const { isLoading: productsLoading } = useDiscountProductsQuery();
  const filteredProductsFromResourceFilters = useFilteredProducts({ requirement });
  const filteredProductsFromSearchString = useMemo(
    () =>
      filteredProductsFromResourceFilters.filter(
        (product) =>
          product.ProductDesc.toLowerCase().includes(searchString.toLowerCase()) ||
          product.ProductNo.toLowerCase().includes(searchString.toLowerCase()) ||
          product.Strain?.toLowerCase().includes(searchString.toLowerCase()) ||
          String(product.Weight).toLowerCase().includes(searchString.toLowerCase()) ||
          product.Category?.toLowerCase().includes(searchString.toLowerCase()) ||
          product.Brand?.toLowerCase().includes(searchString.toLowerCase()) ||
          product.Vendor?.toLowerCase().includes(searchString.toLowerCase())
      ),
    [filteredProductsFromResourceFilters, searchString]
  );
  const columns = useProductListColumns();

  // conditional rendering conditions
  const showNoFiltersAppliedBanner = Object.values(filters).every((req) => req.selectionType === FilterType.NONE);

  return (
    <WizardCardList $fullHeight>
      <FullWidthCardContainer $unpadded>
        <FilterCardWrapper>
          <FilterButtonList>
            {Object.entries(FILTER_RESOURCES).map(([key, val]) => (
              <ResourceFilterButton
                filterButtonClickHandler={filterButtonClickHandler}
                filterKey={key}
                filterValue={val}
                key={val}
                resourceFilter={filters[val]}
              />
            ))}
          </FilterButtonList>
          <ProductListWrapper>
            {showNoFiltersAppliedBanner && (
              <AlertBanner
                marginBottom='0'
                marginTop='0'
                style={AlertBannerStyles.warning}
                text={`There are currently no filters applied. Your entire inventory ${noFilterWarningFragment}.`}
              />
            )}
            <ProductListHeader>
              <ProductListHeading>{heading}</ProductListHeading>
              <ProductListDescription>{description}</ProductListDescription>
            </ProductListHeader>
            <ProductListGridWrapper>
              <ControlBar margin='0 0 var(--sizes-30)'>
                <ControlGroup grow>
                  <ControlSearchBox
                    placeholder='Search products by name, SKU, strain, weight, category, brand, or vendor'
                    onChange={setSearchString}
                  />
                </ControlGroup>
              </ControlBar>
              <ModalDataGrid
                columns={columns}
                disableRowHoverHighlight
                disableSelectionOnClick
                getRowId={(row) => row.ProductId}
                height='100%'
                hideActionsColumn={false}
                loading={productsLoading}
                name={MarketingDiscountsDiscountWizardProductList}
                rows={filteredProductsFromSearchString}
              />
            </ProductListGridWrapper>
          </ProductListWrapper>
        </FilterCardWrapper>
      </FullWidthCardContainer>
    </WizardCardList>
  );
}

function ResourceFilterButton({
  filterKey,
  filterValue,
  filterButtonClickHandler,
  resourceFilter,
}: {
  filterButtonClickHandler: (data: FilterResourceType) => void;
  filterKey: string;
  filterValue: FilterResourceType;
  resourceFilter: FiltersFilterType;
}) {
  const { activeFilterResources } = useFilterResourceData({ filterResourceType: filterValue });

  const formattedSelectedOptions = resourceFilter.resources.map(({ resourceId, resourceName }) => {
    if (resourceName) {
      return resourceName;
    }

    const resource = activeFilterResources.find((resource) => resource.resourceId === resourceId);
    return resource?.resourceName ?? '';
  });

  return (
    <FilterButton
      filterType={resourceFilter.selectionType}
      key={filterValue}
      selectedOptions={formattedSelectedOptions}
      title={filterKey}
      onClick={() => filterButtonClickHandler(filterValue)}
    />
  );
}
